// Hero banner colors (dominion-path visuals, M3): which path a player's
// standard flies, and in what cloth tone. Pure here so it's testable; the
// render half lives in HeroPennants.tsx.
//
// A unit's path is its owner's *dominant* city archetype (majority over the
// owner's cities carrying one); with no archetype cities — a human seat
// before alignment exists — the owner's alignment picks the fallback
// (Light → Industry, Dark → Chaos, matching the sim's spawn default); with
// neither, no banner flies.

import { PATH_ACCENTS } from './territory.js';

export interface BannerSpec {
  /** sRGB hex cloth color (path accent, Dark-toned when alignment is dark). */
  readonly cloth: string;
  /** The archetype the banner flies. */
  readonly archetype: string;
}

function toneDark(hex: string): string {
  const r = Number.parseInt(hex.slice(1, 3), 16) / 255;
  const g = Number.parseInt(hex.slice(3, 5), 16) / 255;
  const b = Number.parseInt(hex.slice(5, 7), 16) / 255;
  const to255 = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 255);
  return `#${[r * 0.42 + 0.02, g * 0.42 + 0.02, b * 0.46 + 0.035]
    .map(to255)
    .map((v) => v.toString(16).padStart(2, '0'))
    .join('')}`;
}

/**
 * The banner path for `ownerId`: majority archetype over their cities, else
 * the alignment default, else null (no banner). Ties break toward the
 * earliest archetype in a stable order so the result is deterministic.
 */
export function bannerFor(
  ownerId: number | undefined,
  cities: ReadonlyArray<Record<string, unknown>>,
): BannerSpec | null {
  if (ownerId === undefined) return null;

  // Majority vote over the owner's archetype-carrying cities. Insertion
  // order of the count map breaks ties (first-seen wins), which is stable
  // for a given snapshot.
  const counts = new Map<string, number>();
  let alignment: string | undefined;
  for (const city of cities) {
    if (city.neutral === true) continue;
    if ((city.ownerId as number | undefined) !== ownerId) continue;
    const a = city.archetype as string | undefined;
    if (a) counts.set(a, (counts.get(a) ?? 0) + 1);
    if (alignment === undefined) alignment = city.alignment as string | undefined;
  }

  let archetype: string | null = null;
  let best = 0;
  for (const [a, n] of counts) {
    if (n > best) {
      archetype = a;
      best = n;
    }
  }
  if (!archetype) {
    archetype = alignment === 'dark' ? 'chaos' : alignment === 'light' ? 'industry' : null;
  }
  if (!archetype) return null;

  const accent = PATH_ACCENTS[archetype];
  if (!accent) return null;
  const to255 = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 255);
  const hex = `#${accent.map((v) => to255(v).toString(16).padStart(2, '0')).join('')}`;
  return { cloth: alignment === 'dark' ? toneDark(hex) : hex, archetype };
}
